// Copied from condi_api/web/admin/src/md5.ts (no browser-native MD5; one call site, no dependency).
/**
 * Minimal MD5 implementation returning a base64 digest, matching the format
 * `uploads.CreateUploadRequest.MD5` expects (it's compared against the
 * object's GCS-reported MD5, which the Google client libraries also report
 * as base64 — see internal/features/uploads/service.go's validateIntegrity).
 *
 * There is no browser-native MD5 (crypto.subtle only offers SHA-*), and this
 * admin panel has no existing hashing dependency, so this is a small
 * self-contained implementation rather than a new dependency for one call site.
 */

function rotateLeft(x: number, c: number): number {
  return (x << c) | (x >>> (32 - c))
}

function toUint32(x: number): number {
  return x >>> 0
}

const S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14,
  20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6,
  10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
]

const K = Array.from({ length: 64 }, (_, i) =>
  toUint32(Math.floor(Math.abs(Math.sin(i + 1)) * 2 ** 32)),
)

function md5ToBytes(data: Uint8Array): Uint8Array {
  const originalLengthBits = data.length * 8
  const paddingLength = (((data.length + 8) >> 6) + 1) * 64
  const padded = new Uint8Array(paddingLength)
  padded.set(data)
  padded[data.length] = 0x80
  const view = new DataView(padded.buffer)
  view.setUint32(paddingLength - 8, toUint32(originalLengthBits), true)
  view.setUint32(paddingLength - 4, Math.floor(originalLengthBits / 2 ** 32), true)

  let a0 = 0x67452301
  let b0 = 0xefcdab89
  let c0 = 0x98badcfe
  let d0 = 0x10325476

  for (let chunkStart = 0; chunkStart < padded.length; chunkStart += 64) {
    const M = new Array<number>(16)
    for (let j = 0; j < 16; j++) {
      M[j] = view.getUint32(chunkStart + j * 4, true)
    }

    let A = a0
    let B = b0
    let C = c0
    let D = d0

    for (let i = 0; i < 64; i++) {
      let F: number
      let g: number
      if (i < 16) {
        F = (B & C) | (~B & D)
        g = i
      } else if (i < 32) {
        F = (D & B) | (~D & C)
        g = (5 * i + 1) % 16
      } else if (i < 48) {
        F = B ^ C ^ D
        g = (3 * i + 5) % 16
      } else {
        F = C ^ (B | ~D)
        g = (7 * i) % 16
      }
      F = toUint32(F + A + K[i]! + M[g]!)
      A = D
      D = C
      C = B
      B = toUint32(B + rotateLeft(F, S[i]!))
    }

    a0 = toUint32(a0 + A)
    b0 = toUint32(b0 + B)
    c0 = toUint32(c0 + C)
    d0 = toUint32(d0 + D)
  }

  const result = new Uint8Array(16)
  const resultView = new DataView(result.buffer)
  resultView.setUint32(0, a0, true)
  resultView.setUint32(4, b0, true)
  resultView.setUint32(8, c0, true)
  resultView.setUint32(12, d0, true)
  return result
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

/** Computes the base64-encoded MD5 digest of a File/Blob's contents. */
export async function md5Base64(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer()
  return bytesToBase64(md5ToBytes(new Uint8Array(buffer)))
}
